import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { syncDocumentsFromChecklist } from '../../../layouts/CriminalRequestLayout';
import { useCriminalWizardContext } from '../../../hooks/useCaseRequestWizard';
import * as criminalService from '../../../services/criminalCaseRequests';
import {
  CASE_DOC_ACCEPT,
  CASE_DOC_DELETED_SUCCESS,
  validateCaseDocumentFile,
} from '../../../utils/caseDocumentUpload';
import { normalizeDocumentFileFields } from '../../../utils/fileDisplayName';
import { setFlashToast } from '../../../utils/flashToast';
import { SectionCard } from '../WizardComponents';
import DocumentPreviewModal from '../../../components/ui/DocumentPreviewModal';
import type { CriminalDocumentRow } from '../../../types/criminalCaseRequest';
import {
  clearCriminalPreviewFile,
  setCriminalPreviewFile,
} from '../../../utils/criminalDocumentPreviewCache';
import { CriminalDocumentUploadRow } from './CriminalDocumentUploadRow';
import { CriminalWizardActions } from './CriminalWizardActions';
import { criminalWizardPath, CriminalWizardShell } from './CriminalWizardShell';
import { focusFirstInvalid } from '../../../utils/requiredValidation';
import { validateStep5Documents } from './criminalStepValidation';
import { applyPmrMandatoryToChecklist, applyPmrMandatoryToDocuments } from '../../../utils/pmrDocumentRule';
import {
  WizardTempFileUploadList,
  type WizardTempUploadedFile,
} from '../../../components/wizard/WizardTempFileUploadList';
import { isDocumentTypeUuid } from '../../../utils/documentTypeId';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

/** Ad-hoc document: uploaded when no checklist exists (no checklist row / document type). */
function isAdHocDocument(doc: CriminalDocumentRow): boolean {
  return !doc.checklistItemId.trim() && !isDocumentTypeUuid(doc.documentTypeId);
}

export default function CriminalStep5UploadDocumentsPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCriminalWizardContext();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [checklistEmpty, setChecklistEmpty] = useState(false);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<CriminalDocumentRow | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteDocId, setPendingDeleteDocId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingDocId = useRef<string | null>(null);

  useEffect(() => {
    if (!draft.fileCategoryId) {
      setLoadingDocs(false);
      return;
    }
    let mounted = true;
    setLoadingDocs(true);
    void criminalService.getDocumentChecklist(draft.fileCategoryId, draft.subFileCategoryId).then((r) => {
      if (!mounted) return;
      if (r.success) {
        setChecklistEmpty(r.data.length === 0);
        setDraft((d) => {
          const checklist = applyPmrMandatoryToChecklist(r.data, d);
          const adHoc = d.documents.filter(isAdHocDocument);
          const checklistDocs = applyPmrMandatoryToDocuments(
            syncDocumentsFromChecklist(d.documents.filter((doc) => !isAdHocDocument(doc)), checklist),
            d,
          );
          return { ...d, documents: [...checklistDocs, ...adHoc] };
        });
      } else {
        setFlashToast({ message: r.message ?? 'Failed to load document checklist.', variant: 'error' });
      }
      setLoadingDocs(false);
    });
    return () => {
      mounted = false;
    };
  }, [draft.fileCategoryId, draft.subFileCategoryId, setDraft]);

  useEffect(() => {
    if (!draft.fileCategoryId || loadingDocs) return;
    setDraft((d) => {
      const documents = applyPmrMandatoryToDocuments(d.documents, d);
      const changed = documents.some((doc, i) => doc.mandatory !== d.documents[i]?.mandatory);
      return changed ? { ...d, documents } : d;
    });
  }, [draft.subFileCategoryId, draft.applicableAreaIds, draft.fileCategoryId, loadingDocs, setDraft]);

  const validate = (): boolean => {
    const extra = validateStep5Documents(draft);
    setErrors(extra);
    if (Object.keys(extra).length === 0) return true;
    const specs = draft.documents
      .filter((d) => extra[`doc-reason-${d.id}`])
      .map((d) => ({
        key: `doc-reason-${d.id}`,
        label: 'Reason',
        value: '',
        elementId: `crim-doc-reason-${d.id}`,
      }));
    focusFirstInvalid(specs, extra);
    return false;
  };

  const goReview = () => {
    if (!validate()) return;
    nav(draft.draftRequestId ? `/requests/new/review/${draft.draftRequestId}` : '/requests/new/review');
  };

  const openFilePicker = (docId: string) => {
    pendingDocId.current = docId;
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const docId = pendingDocId.current;
    e.target.value = '';
    if (!file || !docId) return;

    const validation = validateCaseDocumentFile(file);
    if (!validation.ok) {
      setFlashToast({ message: validation.message, variant: 'error' });
      return;
    }

    setUploadingId(docId);
    setCriminalPreviewFile(docId, file);
    const r = await criminalService.uploadCaseFile(file);
    setUploadingId(null);
    if (!r.success) {
      clearCriminalPreviewFile(docId);
      setFlashToast({ message: r.message ?? 'File upload failed.', variant: 'error' });
      return;
    }

    const { fileName: storedKey, originalFileName: displayName } = normalizeDocumentFileFields(
      r.data.file_name ?? '',
      r.data.original_file_name ?? file.name,
    );
    setDraft((d) => ({
      ...d,
      documents: d.documents.map((doc) =>
        doc.id === docId
          ? {
              ...doc,
              fileName: storedKey,
              originalFileName: displayName || file.name,
              fileSize: r.data.file_size ?? file.size,
              uploadTime: r.data.upload_time ?? new Date().toISOString(),
              skipReason: '',
            }
          : doc,
      ),
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`doc-reason-${docId}`];
      return next;
    });
  };

  const requestRemoveFile = (docId: string) => {
    const doc = draft.documents.find((d) => d.id === docId);
    if (!doc?.fileName) return;
    setPendingDeleteDocId(docId);
    setDeleteConfirmOpen(true);
  };

  const confirmRemoveFile = () => {
    const docId = pendingDeleteDocId;
    if (!docId) {
      setDeleteConfirmOpen(false);
      return;
    }
    clearCriminalPreviewFile(docId);
    setDraft((d) => ({
      ...d,
      documents: d.documents.map((doc) =>
        doc.id === docId
          ? { ...doc, fileName: '', originalFileName: '', fileSize: undefined, uploadTime: '', skipReason: '' }
          : doc,
      ),
    }));
    setDeleteConfirmOpen(false);
    setPendingDeleteDocId(null);
    setFlashToast({ message: CASE_DOC_DELETED_SUCCESS, variant: 'success' });
  };

  const adHocFiles: WizardTempUploadedFile[] = draft.documents.filter(isAdHocDocument).map((d) => ({
    id: d.id,
    fileName: d.fileName,
    originalFileName: d.originalFileName,
    uploadTime: d.uploadTime,
  }));

  const onAdHocFilesChange = (files: WizardTempUploadedFile[]) => {
    setDraft((d) => {
      const checklistDocs = d.documents.filter((doc) => !isAdHocDocument(doc));
      const adHoc: CriminalDocumentRow[] = files.map((file) => {
        const existing = d.documents.find((doc) => doc.id === file.id);
        return {
          id: file.id,
          checklistItemId: '',
          documentTypeId: '',
          documentName: file.originalFileName || file.fileName,
          mandatory: false,
          fileName: file.fileName,
          originalFileName: file.originalFileName,
          fileSize: existing?.fileSize,
          uploadTime: file.uploadTime,
          skipReason: '',
        };
      });
      return { ...d, documents: [...checklistDocs, ...adHoc] };
    });
  };

  return (
    <CriminalWizardShell currentStep={5} draft={draft}>
      <input
        ref={fileInputRef}
        type="file"
        accept={CASE_DOC_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => void onFileSelected(e)}
      />

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title="Delete Document"
        message="Are you sure you want to delete? This action cannot be undone."
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteDocId(null);
        }}
        onConfirm={confirmRemoveFile}
      />

      <SectionCard title={f.sectionUploadDocuments}>
        {loadingDocs ? (
          <p style={{ fontSize: 13, color: 'var(--t2)' }}>{f.loadingChecklist}</p>
        ) : !draft.fileCategoryId ? (
          <p style={{ fontSize: 13, color: 'var(--t2)' }}>Select a file category in Step 1 to load required documents.</p>
        ) : checklistEmpty ? (
          <>
            <p style={{ fontSize: 13, color: 'var(--t2)', marginBottom: 10 }}>
              No document checklist is configured for this file category. You may upload supporting documents below.
            </p>
            <WizardTempFileUploadList
              files={adHocFiles}
              onChange={onAdHocFilesChange}
              uploadFile={criminalService.uploadCaseFile}
              addButtonLabel="Upload Document"
            />
          </>
        ) : draft.documents.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--t2)' }}>No documents are configured for the selected file category.</p>
        ) : (
          draft.documents.map((doc) => (
            <CriminalDocumentUploadRow
              key={doc.id}
              doc={doc}
              uploading={uploadingId === doc.id}
              reasonError={errors[`doc-reason-${doc.id}`]}
              onUploadClick={() => openFilePicker(doc.id)}
              onPreview={() => setPreviewDoc(doc)}
              onRemove={() => requestRemoveFile(doc.id)}
              onReasonChange={(reason) => {
                setDraft((d) => ({
                  ...d,
                  documents: d.documents.map((x) => (x.id === doc.id ? { ...x, skipReason: reason } : x)),
                }));
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next[`doc-reason-${doc.id}`];
                  return next;
                });
              }}
            />
          ))
        )}
      </SectionCard>

      <DocumentPreviewModal
        isOpen={Boolean(previewDoc)}
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />

      <CriminalWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={5}
        onBack={() => nav(criminalWizardPath(4, draft.draftRequestId))}
        showReview
        onNext={goReview}
        validateBeforeSave={validate}
      />
    </CriminalWizardShell>
  );
}
